import {map, mergeMap, retryWhen, scan, takeLast} from "rxjs/operators";
import {Buffer} from "buffer";
import {defer, Observable} from "rxjs";
import {ajax} from "rxjs/ajax";
import _scrypt from "scrypt-js";
import URI from "urijs";
import * as R from 'ramda';
import {APIError} from "../common/errors";

export const API_BASE = '/api/v1';
export const RESULT_LIMIT = 100;
const SALT = 'thereissimplytoomuchmagicatthepantagesacrossthestreet';
const AJAX_RETRY_CSRF = 5;
const AJAX_RETRY_STATUS_0 = 3;
const debugHeaders = {
  // apiKey: 'meow'
};

function getCookie(cname) {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
}

const _ajaxAuthed = (props) => {
  // reason we use defer here is that the CSRF header may change if the call is retried
  return defer(() => {
    let {headers, ...rest} = props;
    return ajax({
      responseType: 'json',
      headers: {...debugHeaders, csrftoken: getCookie('CSRFToken'), ...headers},
      ...rest
    });
  });
};
export const ajaxAuthed = (props, context) => {
  return _ajaxAuthed(props).pipe(
    retryWhen(errors => errors.pipe(
      scan((errorCount, err) => {
        if (err.name === 'AjaxError' &&
          err.status === 0) {
          if (errorCount >= AJAX_RETRY_STATUS_0) throw err;
          return errorCount + 1;
        } else if (err.name === 'AjaxError' &&
          err.status === 403 &&
          err.response &&
          err.response.code &&
          err.response.code === 'invalid_csrf_token') {
          if (errorCount >= AJAX_RETRY_CSRF) throw err;
          return errorCount + 1;
        } else {
          throw err;
        }
      }, 0),
    )),
    map(xhr => ({...xhr, _context: context}))
  );
};

export function verifyAuth$() {
  return ajax({
    url: `${API_BASE}/auth`,
    responseType: 'json',
    method: 'GET',
    withCredentials: true
  });
}

export const scrypt$ = (...args) => Observable.create(obs => {
  _scrypt(...args, (error, progress, key) => {
    if (error) {
      obs.error(error);
    } else if (key) {
      obs.next(key);
      obs.complete();
    } else {
      obs.next(progress);
    }
  });
});

function hashPassword$(pass) {
  let password = new Buffer(pass.normalize('NFKC'));
  let salt = new Buffer(SALT.normalize('NFKC'));
  let N = 1024, r = 8, p = 1, dkLen = 32;
  return scrypt$(password, salt, N, r, p, dkLen).pipe(
    takeLast(1),
    map(pwHash => Buffer.from(pwHash).toString('base64'))
  );
}

export function login$(user, pass) {
  return hashPassword$(pass).pipe(
    mergeMap(pwHash => ajaxAuthed({
      url: `${API_BASE}/auth/login`,
      responseType: 'json',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: {
        username: user,
        password: pwHash
      },
      withCredentials: true
    }))
  );
}

export function logout$() {
  return ajaxAuthed({
    url: `${API_BASE}/auth/logout`,
    responseType: 'json',
    method: 'POST',
    withCredentials: true
  });
}

export function emulateUser$({ id }) {
  let url;
  if (!id) throw new APIError('emulateUser$: no id specified in API call');
  url = URI.joinPaths(API_BASE, 'auth', 'emulate');
  return ajaxAuthed({ url: url.toString(), method: 'POST', body: {id}, headers: { 'Content-Type': 'application/json'} });
}

export function registerAccount$({ sid, user, newPassword }) {
  // sid is the nonce from an email. Take place of authentication
  let query = { sid };
  let url = URI.joinPaths(API_BASE, 'users');
  url.query(URI.buildQuery(query));
  return hashPassword$(newPassword).pipe(
    mergeMap(pwHash => {
      let body = R.set(R.lensProp('password'), pwHash, user);
      let url = URI.joinPaths(API_BASE, 'users');
      url.query(URI.buildQuery({ sid }));
      return ajax({ url: url.toString(), method: 'POST', body,
        headers: { csrftoken: getCookie('CSRFToken'), 'Content-Type': 'application/json'} });
    })
  );
}

export function updatePassword$({ sid, newPassword }) {
  return hashPassword$(newPassword).pipe(
    mergeMap(pwHash => {
      let body = { password: pwHash };
      let query = { sid };
      let url = URI.joinPaths(API_BASE, 'users', 'account', 'password');
      url.query(URI.buildQuery(query));
      return ajax({ url: url.toString(), method: 'PUT', body,
        headers: { csrftoken: getCookie('CSRFToken'), 'Content-Type': 'application/json' }});
    })
  );
}

export function resetPassword$({ email }) {
  let query = { email };
  let url = URI.joinPaths(API_BASE, 'users', 'account', 'password', 'reset');
  url.query(URI.buildQuery(query));
  return ajax({ url: url.toString(), headers: { csrftoken: getCookie('CSRFToken') }, method: 'POST' });
}



// WEBPACK FOOTER //
// ./src/services/common.js